import { useEffect, useMemo, useState } from "react";
import Breadcrumb from "@/components/feature/Breadcrumb";
import WorkCard from "@/components/feature/WorkCard";
import EmptyState from "@/components/feature/EmptyState";
import { works } from "@/mocks/works";
import { useDocumentMeta } from "@/hooks/useDocumentMeta";

const typeOptions = ["全部", "电影", "电视剧"];
const statusOptions = ["全部状态", "已上映", "即将上线", "拍摄中"];
const yearOptions = ["全部年份", "2026", "2025", "2024", "2023", "2022"];
const sortOptions = [
  { key: "latest", label: "最新发布" },
  { key: "rating", label: "评分最高" },
  { key: "heat", label: "热度最高" },
];

const PAGE_SIZE = 8;

export default function Works() {
  useDocumentMeta(
    "吴梦梦影视作品 - 电影电视剧分类列表",
    "吴梦梦影视作品分类列表，收录其电影与电视剧作品，可按类型、上映年份、上映状态筛选，并按发布时间、评分与热度排序查看。",
    "吴梦梦作品,吴梦梦电影,吴梦梦电视剧,影视作品列表,作品筛选",
  );

  const [type, setType] = useState("全部");
  const [year, setYear] = useState("全部年份");
  const [status, setStatus] = useState("全部状态");
  const [sort, setSort] = useState("latest");
  const [visible, setVisible] = useState(PAGE_SIZE);
  const [filtersOpen, setFiltersOpen] = useState(false);

  useEffect(() => {
    setVisible(PAGE_SIZE);
  }, [type, year, status, sort]);

  const filtered = useMemo(() => {
    const list = works.filter((work) => {
      if (type !== "全部" && work.type !== type) return false;
      if (year !== "全部年份" && String(work.year) !== year) return false;
      if (status !== "全部状态" && work.status !== status) return false;
      return true;
    });

    return [...list].sort((a, b) => {
      if (sort === "rating") return b.rating - a.rating;
      if (sort === "heat") return b.heat - a.heat;
      return b.releaseDate.localeCompare(a.releaseDate);
    });
  }, [type, year, status, sort]);

  const displayed = filtered.slice(0, visible);

  const resetFilters = () => {
    setType("全部");
    setYear("全部年份");
    setStatus("全部状态");
    setSort("latest");
  };

  const activeFilterCount =
    (type !== "全部" ? 1 : 0) +
    (year !== "全部年份" ? 1 : 0) +
    (status !== "全部状态" ? 1 : 0);

  const selectClass =
    "appearance-none w-full md:w-auto h-10 pl-3 pr-9 rounded-md border border-background-300 bg-background-50 text-sm text-foreground-800 focus:outline-none focus:border-primary-400 cursor-pointer";

  return (
    <div>
      <section className="border-b border-background-200 bg-background-100/60">
        <div className="container-content py-7 md:py-11">
          <Breadcrumb items={[{ label: "首页", to: "/" }, { label: "作品" }]} />
          <div className="mt-4 md:mt-5 flex flex-col lg:flex-row lg:items-end justify-between gap-5">
            <div className="min-w-0">
              <span className="text-[10px] md:text-[11px] font-label tracking-[0.22em] text-accent-600 uppercase">
                Works Archive
              </span>
              <h1 className="mt-2 text-2xl md:text-4xl font-bold leading-tight">吴梦梦影视作品</h1>
              <p className="mt-3 max-w-2xl text-sm text-foreground-600 leading-relaxed">
                收录电影与电视剧共 {works.length} 部。电影偏向作者表达与类型手法的结合，电视剧则集中在年代群像与都市现实题材。
                使用下方筛选与排序，快速定位你想了解的作品。
              </p>
            </div>
            <div className="shrink-0 flex items-center gap-5">
              <div>
                <div className="font-label text-2xl font-extrabold text-primary-600">
                  {works.filter((work) => work.type === "电影").length}
                </div>
                <div className="mt-1 text-[11px] text-foreground-500">电影作品</div>
              </div>
              <div className="w-px h-9 bg-background-300" />
              <div>
                <div className="font-label text-2xl font-extrabold text-primary-600">
                  {works.filter((work) => work.type === "电视剧").length}
                </div>
                <div className="mt-1 text-[11px] text-foreground-500">电视剧作品</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="container-content py-7 md:py-10">
        <div className="rounded-lg border border-background-200 bg-background-50">
          <div className="flex items-center justify-between gap-3 px-4 md:px-5 py-3.5 border-b border-background-200 md:border-b-0">
            <div className="flex items-center gap-2 min-w-0">
              <i className="ri-filter-3-line text-foreground-500 leading-none" />
              <span className="text-sm font-medium text-foreground-900 whitespace-nowrap">筛选作品</span>
              {activeFilterCount > 0 && (
                <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-accent-100 text-accent-800 font-label">
                  {activeFilterCount}
                </span>
              )}
              <span className="hidden md:inline text-xs text-foreground-500 ml-1">
                共 {filtered.length} 部符合条件
              </span>
            </div>
            <button
              type="button"
              onClick={() => setFiltersOpen((open) => !open)}
              className="md:hidden inline-flex items-center gap-1 h-8 px-3 rounded-md border border-background-300 text-xs text-foreground-700 cursor-pointer whitespace-nowrap"
            >
              {filtersOpen ? "收起" : "展开"}
              <i className={`${filtersOpen ? "ri-arrow-up-s-line" : "ri-arrow-down-s-line"} text-sm leading-none`} />
            </button>
          </div>

          <div className={`${filtersOpen ? "block" : "hidden"} md:block px-4 md:px-5 py-4 border-b border-background-200`}>
            <div className="flex flex-col lg:flex-row lg:items-center gap-4 lg:gap-6">
              <div className="flex flex-col sm:flex-row sm:items-center gap-3">
                <span className="shrink-0 text-xs text-foreground-500 w-14">类型</span>
                <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 w-full sm:w-auto">
                  {typeOptions.map((option) => (
                    <button
                      key={option}
                      type="button"
                      onClick={() => setType(option)}
                      className={`flex-1 sm:flex-none px-3.5 h-8 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                        type === option
                          ? "bg-primary-500 text-background-50 font-medium"
                          : "text-foreground-600 hover:text-foreground-950"
                      }`}
                    >
                      {option}
                    </button>
                  ))}
                </div>
              </div>

              <div className="flex flex-col sm:flex-row gap-3 flex-1">
                <div className="flex items-center gap-3 flex-1">
                  <span className="shrink-0 text-xs text-foreground-500 w-14 sm:w-auto">年份</span>
                  <div className="relative flex-1">
                    <select value={year} onChange={(event) => setYear(event.target.value)} className={selectClass} aria-label="按年份筛选">
                      {yearOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 leading-none pointer-events-none" />
                  </div>
                </div>
                <div className="flex items-center gap-3 flex-1">
                  <span className="shrink-0 text-xs text-foreground-500 w-14 sm:w-auto">状态</span>
                  <div className="relative flex-1">
                    <select value={status} onChange={(event) => setStatus(event.target.value)} className={selectClass} aria-label="按上映状态筛选">
                      {statusOptions.map((option) => (
                        <option key={option} value={option}>
                          {option}
                        </option>
                      ))}
                    </select>
                    <i className="ri-arrow-down-s-line absolute right-3 top-1/2 -translate-y-1/2 text-foreground-400 leading-none pointer-events-none" />
                  </div>
                </div>
              </div>

              {activeFilterCount > 0 && (
                <button
                  type="button"
                  onClick={resetFilters}
                  className="self-start lg:self-auto inline-flex items-center gap-1 h-8 px-3 rounded-md text-xs text-accent-700 hover:bg-accent-50 cursor-pointer whitespace-nowrap"
                >
                  <i className="ri-refresh-line text-sm leading-none" />
                  重置筛选
                </button>
              )}
            </div>
          </div>

          <div className="px-4 md:px-5 py-3 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
            <span className="text-xs text-foreground-500 md:hidden">
              共 {filtered.length} 部符合条件
            </span>
            <div className="hidden md:block text-xs text-foreground-500">
              排序方式
            </div>
            <div className="inline-flex items-center gap-1 p-1 rounded-full bg-background-100 self-start">
              {sortOptions.map((option) => (
                <button
                  key={option.key}
                  type="button"
                  onClick={() => setSort(option.key)}
                  className={`px-3.5 h-8 rounded-full text-xs transition-colors cursor-pointer whitespace-nowrap ${
                    sort === option.key
                      ? "bg-background-50 text-foreground-950 font-medium border border-background-200"
                      : "text-foreground-600 hover:text-foreground-950"
                  }`}
                >
                  {option.label}
                </button>
              ))}
            </div>
          </div>
        </div>

        <div className="mt-6 md:mt-8">
          {displayed.length === 0 ? (
            <EmptyState
              icon="ri-film-line"
              title="没有找到符合条件的作品"
              description="试着放宽年份或上映状态的筛选条件，或者直接查看全部作品档案。"
              actionLabel="重置筛选条件"
              onAction={resetFilters}
            />
          ) : (
            <>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3 md:gap-5">
                {displayed.map((work) => (
                  <WorkCard key={work.id} work={work} />
                ))}
              </div>

              <div className="mt-9 flex flex-col items-center gap-3">
                <p className="text-xs text-foreground-500 font-label">
                  已显示 {displayed.length} / {filtered.length} 部作品
                </p>
                {visible < filtered.length ? (
                  <button
                    type="button"
                    onClick={() => setVisible((count) => count + 4)}
                    className="inline-flex items-center gap-2 h-11 px-7 rounded-md border border-background-300 bg-background-50 text-sm font-medium text-foreground-800 hover:border-primary-400 hover:text-primary-600 transition-colors cursor-pointer whitespace-nowrap"
                  >
                    加载更多作品
                    <i className="ri-arrow-down-line text-base leading-none" />
                  </button>
                ) : (
                  <span className="inline-flex items-center gap-1.5 text-xs text-foreground-400">
                    <i className="ri-check-line text-sm leading-none" />
                    已经到底了
                  </span>
                )}
              </div>
            </>
          )}
        </div>
      </section>
    </div>
  );
}